import { useEffect, useRef } from 'react';
import Room from './Room';
import EntryStandby from './EntryStandby';
import { useConnectionManager } from '@/lib/hooks/useConnectionManager';
import { ConnectionState } from '@/lib/core';

import logger from '@/utils/logger';
import styles from './index.module.less';

function MainArea() {
  // URL 场景解析放在上层 MainPage 统一进行，这里仅通过 props/URL 读取参数
  const urlParams = new URLSearchParams(window.location.search);
  const directRoomFromURL = urlParams.get('DirectRoom') === 'true';
  const questionFromURL = urlParams.get('Question');
  const autoStartFromURL = urlParams.get('AutoStart') === 'true';

  // 使用统一的连接管理器
  const {
    state: connectionState,
    isConnecting,
    isConnected,
    hasError,
    connect,
  } = useConnectionManager();

  // 防重复连接的ref
  const hasAutoConnected = useRef(false);

  // DirectRoom自动连接逻辑（一次性防抖 + 状态保护）
  useEffect(() => {
    if (
      directRoomFromURL &&
      connectionState === ConnectionState.IDLE &&
      !hasAutoConnected.current
    ) {
      logger.info('[MainArea] 检测到DirectRoom参数，开始自动连接...');
      hasAutoConnected.current = true;

      connect({
        directRoom: true,
        question: questionFromURL || undefined,
        autoStart: autoStartFromURL,
      })
        .then((result) => {
          if (result.success) {
            logger.info('[MainArea] DirectRoom自动连接成功');
          } else {
            logger.error('[MainArea] DirectRoom自动连接失败:', result.error);
            // 连接失败时重置标记，允许重试
            hasAutoConnected.current = false;
          }
        })
        .catch((error) => {
          logger.error('[MainArea] DirectRoom自动连接异常:', error);
          // 连接异常时重置标记，允许重试
          hasAutoConnected.current = false;
        });
    }
  }, [directRoomFromURL, connectionState, connect, questionFromURL, autoStartFromURL]);

  // 当连接状态重置时，重置自动连接标记
  useEffect(() => {
    if (connectionState === ConnectionState.IDLE) {
      hasAutoConnected.current = false;
    }
  }, [connectionState]);

  // 统一的页面渲染逻辑 - 不再区分DirectRoom和普通模式
  // 所有状态判断都基于ConnectionManager的状态

  // 渲染层日志去重：仅当状态切换到 Entry 条件时记录一次
  useEffect(() => {
    if (!isConnected && !isConnecting && (hasError || connectionState === ConnectionState.IDLE)) {
      logger.info(`[MainArea] 显示Entry页面，状态: ${connectionState}`);
    }
  }, [isConnected, isConnecting, hasError, connectionState]);

  // 未连接（idle/connecting/error）统一用同一容器承接，避免 EntryStandby 在状态切换时 remount 导致闪烁
  if (!isConnected) {
    return (
      <div className={styles.connectingContainer}>
        <EntryStandby />
        {isConnecting && <div className={styles.connectingBanner}>正在连接中…</div>}
      </div>
    );
  }

  // 连接成功，显示Room
  if (isConnected) {
    return <Room />;
  }

  // 兜底：不应命中（已在上方统一容器返回）
  return <Room />;
}

export default MainArea;
